/**
 * Canonical first/last-name resolution.
 *
 * `Users/{uid}.profile.firstName` + `.profile.lastName` (separate fields) is the single
 * source of truth (the anchor). Legacy/combined shapes exist across docs, so this resolver
 * derives a first + last name from ANY known location — explicit fields across
 * profile / blueprint.personalInfo / blueprint-root, or a combined
 * `name`/`displayName`/`fullName` split into first + rest — so activation, completeness,
 * and display never fail just because a name was stored in a different shape or document.
 *
 * Both names are required (mandatory real name): a single-word name yields an empty
 * lastName, i.e. NOT a full name (`hasFullName` is false).
 */
export function resolveName(sources = {}) {
  const { profile = {}, personalInfo = {}, blueprint = {} } = sources || {};
  const pick = (...vals) => vals.find((v) => typeof v === "string" && v.trim());

  let firstName = pick(profile.firstName, personalInfo.firstName, blueprint.firstName);
  let lastName = pick(profile.lastName, personalInfo.lastName, blueprint.lastName);

  if (!(firstName && lastName)) {
    const combined = pick(
      profile.name, profile.displayName, profile.fullName,
      personalInfo.name, personalInfo.fullName,
      blueprint.name, blueprint.fullName
    );
    if (combined) {
      const parts = combined.trim().split(/\s+/);
      firstName = firstName || parts[0];
      lastName = lastName || (parts.length > 1 ? parts.slice(1).join(" ") : "");
    }
  }

  return { firstName: (firstName || "").trim(), lastName: (lastName || "").trim() };
}

/** True only when both a first and last name resolved. */
export const hasFullName = (name) => !!(name && name.firstName && name.lastName);
